import type { Theme, CSSObject, MixinsOptions } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { grey } from '../palette';
import { borderGradient } from './border';
import { maxLine, textGradient } from './text';
import { bgBlur, bgGradient } from './background';
import { softStyles, paperStyles, filledStyles, menuItemStyles } from './global-styles-components';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../../extend-theme-types.d.ts}
 */

export type * from './text';
export type * from './border';
export type * from './background';
export type * from './global-styles-components';

export type MixinsExtend = {
  hideScrollX: CSSObject;
  hideScrollY: CSSObject;
  scrollbarStyles: (theme: Theme) => CSSObject;
  bgBlur: typeof bgBlur;
  maxLine: typeof maxLine;
  bgGradient: typeof bgGradient;
  softStyles: typeof softStyles;
  paperStyles: typeof paperStyles;
  textGradient: typeof textGradient;
  filledStyles: typeof filledStyles;
  borderGradient: typeof borderGradient;
  menuItemStyles: typeof menuItemStyles;
};

/**
 * Clear space between a scroll container's content and its scrollbar track.
 * The track sits at the border edge, so only padding can hold that space.
 * Apply it per container (`pr`); it is not part of the mixin, which would
 * change the padding of every existing caller.
 */
export const SCROLLBAR_GAP_PX = 8;

/** A flex column that takes the free height of its parent flex column. */
export const fillColumnSx = { flex: '1 1 0px', display: 'flex', flexDirection: 'column' } as const;

/**
 * Thin scrollbar styles for containers with dark backgrounds.
 * Spread into `sx` props: `sx={{ overflow: 'auto', ...thinScrollbarSx }}`
 * iOS Safari ignores ::-webkit-scrollbar entirely (native overlay only) —
 * this styling is fully effective on Chrome/Android and desktop WebKit.
 */
const SCROLLBAR_THUMB_COLOR = varAlpha(grey['600Channel'], 0.6);

export const thinScrollbarSx = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_THUMB_COLOR} transparent`,
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
    background: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    boxShadow: 'none',
    border: 'none',
    visibility: 'hidden',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: SCROLLBAR_THUMB_COLOR,
    borderRadius: '100px',
    visibility: 'visible',
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
} as const;

/* **********************************************************************
 * 📦 Final
 * **********************************************************************/
export const mixins: MixinsOptions = {
  hideScrollX: {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    overflowX: 'auto',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  hideScrollY: {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    overflowY: 'auto',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  scrollbarStyles: (theme: Theme): CSSObject => ({
    scrollbarWidth: 'thin',
    scrollbarColor: `${varAlpha(theme.vars.palette.text.disabledChannel, 0.4)} ${varAlpha(
      theme.vars.palette.text.disabledChannel,
      0.08
    )}`,
  }),
  bgBlur,
  maxLine,
  bgGradient,
  softStyles,
  paperStyles,
  textGradient,
  filledStyles,
  borderGradient,
  menuItemStyles,
};
